import { InfoCircleOutlined } from '@ant-design/icons'
import { regexp } from '@lmc/common/constants'
import type { ResidentItem } from '@lmc/ts-common'
import {
  Chevron,
  FormCard,
  FormItem,
  FormRadio,
  FormRadioGroup,
  Input,
  Select,
  TextArea,
  theme
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { MentalCapacityToConsentSchema } from '@lmc/validations'
import { Form, Tooltip } from 'antd'
import { BLOOD_TYPE, MENTAL_CAPACITY_OPTIONS } from 'common/constants'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  healthCareNumberValidator,
  nhsNumberValidator
} from 'screens/Residents/screens/ResidentProfileScreen/utils/validators'
import styled from 'styled-components'
import { SupDatePicker } from '../../../styledComponents'
import {
  dateFormatTime,
  getNhsTraceStatusExtra,
  getSelectOptions,
  toFeetAndInches
} from '../../../utils/utils'
import { MaskInput } from './components/MaskInput'

type GeneralInfoFormCardProps = {
  resident: ResidentItem
  isFromCanada: boolean
  canDeleteNhsNumber: boolean
  isAdmin: boolean
  preferredTerm: string
  mentalCapacityEnabled?: boolean
}

export const GeneralInfoFormCard = ({
  resident,
  isFromCanada,
  canDeleteNhsNumber,
  isAdmin,
  preferredTerm,
  mentalCapacityEnabled
}: GeneralInfoFormCardProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const mentalCapacityValue = Form.useWatch('mentalCapacityToConsent')
  const parseHeight = (height: string | undefined) => {
    const parsedHeight = Number(height)
    return !Number.isNaN(parsedHeight) ? toFeetAndInches(parsedHeight) : null
  }

  const [heightInFeetAndInches, setHeightInFeetAndInches] = useState(
    parseHeight(resident?.height)
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setHeightInFeetAndInches(parseHeight(resident?.height))
  }, [resident?.height])

  const traceStatusExtra = getNhsTraceStatusExtra(
    resident,
    t(supConstants.NHS_NUMBER_STATUS_INFO)
  )

  return (
    <FormCard title={t(supConstants.GENERAL_INFORMATION)}>
      {mentalCapacityEnabled && (
        <FormItem
          label={
            <Text>
              {t(supConstants.MENTAL_CAPACITY_TO_CONSENT, {
                variable: preferredTerm
              })}{' '}
              <Tooltip
                title={t(supConstants.MENTAL_CAPACITY_TOOLTIP, {
                  variable: preferredTerm
                })}
              >
                <InfoCircleOutlined
                  style={{ color: theme.color.neutral.color5 }}
                />
              </Tooltip>
            </Text>
          }
          name="mentalCapacityToConsent"
          extra={
            mentalCapacityValue ===
            MentalCapacityToConsentSchema.enum.in_progress ? (
              <BaseText level={2}>
                {t(supConstants.MENTAL_CAPACITY_ASSESSMENT_IN_PROGRESS)}
              </BaseText>
            ) : undefined
          }
        >
          <Select
            suffixIcon={
              <Chevron direction="down" color={theme.color.neutral.color5} />
            }
            options={getSelectOptions(MENTAL_CAPACITY_OPTIONS)}
            disabled={!isAdmin}
            allowClear
          />
        </FormItem>
      )}
      <FormItem
        label={<Text>{t(supConstants.RISK_OF_CHOKING)}</Text>}
        name="hasChokingRisk"
      >
        <FormRadioGroup>
          <FormRadio value={false} text={t(supConstants.NO_RISK)} />
          <FormRadio value text={t(supConstants.THERE_IS_A_RISK)} />
        </FormRadioGroup>
      </FormItem>
      <HeightContainer>
        <FormItem label={<Text>{t(supConstants.HEIGHT)}</Text>} name="height">
          <Input
            type="number"
            min={1}
            max={250}
            suffix={t(supConstants.CM)}
            onChange={(e) => {
              setHeightInFeetAndInches(toFeetAndInches(Number(e.target.value)))
            }}
            defaultValue={resident.height || ''}
          />
        </FormItem>
        <BaseText>{heightInFeetAndInches}</BaseText>
      </HeightContainer>
      <FormItem
        label={<Text>{t(supConstants.BLOOD_TYPE)}</Text>}
        name="bloodType"
      >
        <Select
          suffixIcon={
            <Chevron direction="down" color={theme.color.neutral.color5} />
          }
          options={getSelectOptions(BLOOD_TYPE)}
        />
      </FormItem>
      <FormItem
        label={<Text>{t(supConstants.MEDICAL_HISTORY)}</Text>}
        name="medicalHistory"
      >
        <TextArea
          autoSize={{
            minRows: 3
          }}
        />
      </FormItem>
      <FormItem
        name="admissionDate"
        label={<Text>{t(supConstants.ADMISSION_DATE)}</Text>}
      >
        <SupDatePicker placeholder="" format={dateFormatTime} showTime />
      </FormItem>
      {!isFromCanada && (
        <FormItem
          label={<Text>{t(supConstants.NATIONAL_INSURANCE_NUMBER)}</Text>}
          name="nationalInsurance"
          colon={false}
          rules={[
            {
              pattern: regexp.nationalInsurance,
              message: t(supConstants.NATIONAL_INSURANCE_NUMBER_ERROR)
            }
          ]}
        >
          <Input />
        </FormItem>
      )}
      {isFromCanada ? (
        <FormItem
          label={<Text>{t(supConstants.HEALTH_CARE_NUMBER)}</Text>}
          name="nhsNumber"
          rules={[{ validator: healthCareNumberValidator }]}
        >
          <MaskInput mask="000 000 000 000" />
        </FormItem>
      ) : (
        <FormItem
          label={<Text>{t(supConstants.NHS_NUMBER)}</Text>}
          name="nhsNumber"
          extra={traceStatusExtra}
          rules={[
            { validator: nhsNumberValidator },
            ...(!canDeleteNhsNumber && resident?.nhsNumber
              ? [
                  {
                    required: true,
                    message: t(supConstants.NHS_NUMBER_REQUIRED_FOR_ROLE)
                  }
                ]
              : [])
          ]}
        >
          <MaskInput mask="000 000 0000" />
        </FormItem>
      )}
    </FormCard>
  )
}

const HeightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`

const BaseText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`
